import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Package, User as UserIcon, ChevronDown, MapPin, Loader2, Check, RotateCcw } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { TrackingTimeline } from "../components/TrackingTimeline";

const CITIES = ["Accra", "Tema", "Kumasi"];

export default function Account() {
  const { user, logout, updateProfile } = useAuth();
  const { add } = useCart();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);
  useEffect(() => {
    if (user && user.email) api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]);

  const reorder = async (order) => {
    if (order.type !== "cart") { navigate("/boxes"); return; }
    let added = 0;
    for (const it of order.items) {
      if (!it.product_id) continue;
      try {
        const { data } = await api.get(`/products/${it.product_id}`);
        add(data, it.quantity || 1);
        added += 1;
      } catch {}
    }
    if (added) { toast.success("Items added to cart"); navigate("/cart"); }
    else toast.error("These items are no longer available");
  };

  if (!user || !user.email) return <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 text-[#306D29]/70">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-10">
        <aside className="h-fit space-y-4">
          <div className="rounded-3xl bg-[#0D530E] text-[#FBF5DD] p-7">
            {user.picture
              ? <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover mb-4" />
              : <div className="w-16 h-16 rounded-full bg-[#E7E1B1] text-[#0D530E] flex items-center justify-center font-display text-2xl mb-4">{user.name ? user.name[0].toUpperCase() : <UserIcon size={22} />}</div>}
            <p className="font-display text-2xl">{user.name}</p>
            <p className="text-[#FBF5DD]/60 text-sm font-light break-all">{user.email}</p>
          </div>
          <nav className="rounded-3xl bg-white border border-[#0D530E]/8 p-2">
            {[["orders", "My Orders", Package], ["profile", "Profile & Address", MapPin]].map(([k, label, Icon]) => (
              <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium ${tab === k ? "bg-[#E7E1B1]/60 text-[#0D530E]" : "text-[#306D29]/80 hover:bg-[#E7E1B1]/30"}`}
                style={{ transition: "background-color 0.2s ease" }}>
                <Icon size={17} /> {label}
              </button>
            ))}
            <button data-testid="account-logout" onClick={() => { logout(); navigate("/"); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-[#B0341F] hover:bg-[#B0341F]/8" style={{ transition: "background-color 0.2s ease" }}>
              <LogOut size={17} /> Sign out
            </button>
          </nav>
        </aside>

        <div>
          {tab === "orders" ? (
            <>
              <h1 className="font-display text-4xl sm:text-5xl font-light text-[#0D530E] mb-2">My orders</h1>
              <p className="text-[#306D29]/80 font-light mb-8">Track your deliveries and reorder your favourites.</p>
              {orders.length === 0 ? (
                <div className="rounded-3xl bg-[#E7E1B1]/30 border border-[#0D530E]/5 p-12 text-center" data-testid="account-no-orders">
                  <Package size={30} className="text-[#306D29]/50 mx-auto mb-4" />
                  <p className="font-display text-2xl text-[#0D530E] mb-2">No orders yet</p>
                  <p className="text-[#306D29]/70 font-light mb-6">When you place an order it will appear here with live tracking.</p>
                  <Link to="/shop" className="inline-flex bg-[#306D29] text-[#FBF5DD] font-medium px-7 py-3 rounded-full">Start shopping</Link>
                </div>
              ) : (
                <div className="space-y-4" data-testid="account-orders">
                  {orders.map((o) => (
                    <div key={o.order_number} className="rounded-3xl bg-white border border-[#0D530E]/10 overflow-hidden">
                      <button data-testid={`order-${o.order_number}`} onClick={() => setExpanded(expanded === o.order_number ? null : o.order_number)}
                        className="w-full flex items-center justify-between gap-3 p-6 text-left">
                        <div className="flex items-center gap-4 min-w-0">
                          <img src={o.items[0]?.image} alt="" className="w-14 h-14 rounded-xl object-cover bg-[#E7E1B1]" />
                          <div className="min-w-0">
                            <p className="font-display text-xl text-[#0D530E]">{o.order_number}</p>
                            <p className="text-sm text-[#306D29]/70 truncate">{o.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")}</p>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-display text-lg text-[#306D29]">GH₵ {o.total_ghs.toLocaleString()}</p>
                          <span className={`text-xs font-medium ${o.payment_status === "paid" ? "text-[#306D29]" : "text-[#D9933B]"}`}>{o.payment_status === "paid" ? "● Paid" : "○ Pending"}</span>
                          <ChevronDown size={16} className={`inline ml-2 text-[#306D29] ${expanded === o.order_number ? "rotate-180" : ""}`} style={{ transition: "transform 0.2s ease" }} />
                        </div>
                      </button>
                      {expanded === o.order_number && (
                        <div className="px-6 pb-6 pt-2 border-t border-[#0D530E]/10">
                          {o.payment_status === "paid" ? <TrackingTimeline steps={o.steps} currentStep={o.current_step} /> : <p className="text-[#D9933B] py-4">Awaiting payment confirmation.</p>}
                          {o.payment_status === "paid" && (
                            <button data-testid={`reorder-${o.order_number}`} onClick={() => reorder(o)}
                              className="mt-2 inline-flex items-center gap-2 bg-[#E7E1B1] hover:bg-[#d9d19a] text-[#0D530E] text-sm font-medium px-5 py-2.5 rounded-full" style={{ transition: "background-color 0.2s ease" }}>
                              <RotateCcw size={15} /> {o.type === "cart" ? "Reorder" : "Order again"}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <ProfileForm user={user} onSave={updateProfile} cities={CITIES} />
          )}
        </div>
      </div>
    </div>
  );
}

function ProfileForm({ user, onSave, cities }) {
  const [f, setF] = useState({
    name: user.name || "", phone: user.phone || "", city: user.city || cities[0],
    address: user.address || "", digital_address: user.digital_address || "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setSaved(false); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(f); setSaved(true); toast.success("Profile saved"); }
    catch { toast.error("Could not save profile"); }
    finally { setSaving(false); }
  };

  const input = "w-full bg-white border border-[#0D530E]/15 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#306D29] focus:outline-none";
  const label = "text-xs tracking-[0.15em] uppercase font-semibold text-[#306D29] mb-1.5 block";

  return (
    <>
      <h1 className="font-display text-4xl sm:text-5xl font-light text-[#0D530E] mb-2">Profile & address</h1>
      <p className="text-[#306D29]/80 font-light mb-8">Save your details for faster checkout and accurate delivery.</p>
      <form onSubmit={submit} className="rounded-3xl bg-white border border-[#0D530E]/10 p-8 space-y-5 max-w-2xl" data-testid="profile-form">
        <div><label className={label}>Full name</label><input className={input} value={f.name} onChange={set("name")} data-testid="pf-name" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div><label className={label}>Phone</label><input className={input} placeholder="024 000 0000" value={f.phone} onChange={set("phone")} data-testid="pf-phone" /></div>
          <div><label className={label}>City</label><select className={input} value={f.city} onChange={set("city")} data-testid="pf-city">{cities.map((c) => <option key={c}>{c}</option>)}</select></div>
        </div>
        <div><label className={label}>Delivery address</label><textarea rows={3} className={input} placeholder="House number, street, area / landmark" value={f.address} onChange={set("address")} data-testid="pf-address" /></div>
        <div>
          <label className={label}>Ghana digital address (GhanaPost GPS)</label>
          <input className={input} placeholder="e.g. GA-123-4567" value={f.digital_address} onChange={set("digital_address")} data-testid="pf-digital" />
          <p className="text-xs text-[#306D29]/60 mt-1.5">Your GhanaPost GPS code helps riders find you precisely.</p>
        </div>
        <button type="submit" disabled={saving} data-testid="pf-save"
          className="bg-[#306D29] hover:bg-[#0D530E] text-[#FBF5DD] font-medium px-8 py-3.5 rounded-full flex items-center gap-2 disabled:opacity-60"
          style={{ transition: "background-color 0.2s ease" }}>
          {saving ? <Loader2 size={17} className="animate-spin" /> : saved ? <Check size={17} /> : null}
          {saved ? "Saved" : "Save changes"}
        </button>
      </form>
    </>
  );
}
